import { z } from "zod";

/**
 * Glassdoor-style reviews + salaries on /organizations/[key].
 *
 * Anonymity contract: a review stores no user id and no email, and the page
 * shows only month + year. Orgs that claim their page get nothing more.
 *
 * Sample rows (`isSample`) are written by us to show the format: a local
 * design aid, rendered only when SHOW_SAMPLE_REVIEWS=1, never on the public site.
 */

export const REVIEW_HONEYPOT_FIELD = "faxLine";

export const REVIEW_CONTRACT_TYPES = ["full_time", "part_time", "contract", "intern"] as const;

export const CONTRACT_LABELS: Record<(typeof REVIEW_CONTRACT_TYPES)[number], string> = {
  full_time: "Full-time",
  part_time: "Part-time",
  contract: "Contractor",
  intern: "Intern",
};

export const SALARY_CURRENCIES = ["USD", "EUR", "GBP", "CHF", "CAD", "AUD", "SGD", "HKD"] as const;

const optText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((v) => (v ? v : undefined));

export const reviewInputSchema = z
  .object({
    rating: z.coerce.number().int().min(1, "Pick a rating.").max(5),
    title: z.string().trim().min(3, "Add a short headline.").max(120),
    pros: z.string().trim().min(20, "Pros: at least 20 characters.").max(3000),
    cons: z.string().trim().min(20, "Cons: at least 20 characters.").max(3000),
    jobTitle: z.string().trim().min(2, "Your job title is required.").max(120),
    grade: optText(30),
    contractType: z.enum(REVIEW_CONTRACT_TYPES).optional(),
    employment: z.enum(["current", "former"], { error: "Current or former?" }),
    location: optText(80),
    salaryAmount: z
      .union([z.literal(""), z.coerce.number().int().min(100).max(5_000_000)])
      .optional()
      .transform((v) => (v === "" || v == null ? undefined : v)),
    salaryCurrency: z.enum(SALARY_CURRENCIES).optional(),
  })
  .refine((d) => d.salaryAmount == null || d.salaryCurrency != null, {
    path: ["salaryCurrency"],
    message: "Which currency?",
  });

export type ReviewInput = z.infer<typeof reviewInputSchema>;

export const claimInputSchema = z.object({
  name: z.string().trim().min(1, "Your name is required.").max(200),
  email: z.string().trim().email("Enter your work email.").max(254),
  role: optText(120),
});

export type FormResult = { ok: true } | { ok: false; errors: Record<string, string> };

export function fieldErrors(error: z.ZodError): Record<string, string> {
  const out: Record<string, string> = {};
  for (const issue of error.issues) {
    const key = issue.path[0] != null ? String(issue.path[0]) : "_form";
    if (!(key in out)) out[key] = issue.message;
  }
  return out;
}

/** True when the email's domain is the org website's domain or a subdomain of it. */
export function emailMatchesWebsite(email: string, website: string | null): boolean {
  if (!website) return false;
  let host: string;
  try {
    host = new URL(website).hostname.replace(/^www\./, "").toLowerCase();
  } catch {
    return false;
  }
  const domain = email.split("@")[1]?.toLowerCase() ?? "";
  return domain === host || domain.endsWith(`.${host}`);
}

export interface ReviewRow {
  id: number;
  isSample: boolean;
  rating: number;
  title: string;
  pros: string;
  cons: string;
  jobTitle: string;
  grade: string | null;
  contractType: string | null;
  employment: string;
  location: string | null;
  salaryAmount: number | null;
  salaryCurrency: string | null;
  createdAt: Date | null;
}

export interface SalaryGroup {
  label: string;
  currency: string;
  count: number;
  median: number;
  min: number;
  max: number;
}

export interface OrgReviewSummary {
  /** true when the numbers below come from sample rows only. */
  fromSamples: boolean;
  reviewCount: number;
  average: number | null;
  /** index 0 = 1 star … index 4 = 5 stars */
  distribution: [number, number, number, number, number];
  salaries: SalaryGroup[];
}

function median(sorted: number[]): number {
  const mid = sorted.length >> 1;
  return sorted.length % 2 ? sorted[mid]! : Math.round((sorted[mid - 1]! + sorted[mid]!) / 2);
}

export function summarize(rows: ReviewRow[]): OrgReviewSummary {
  const real = rows.filter((r) => !r.isSample);
  const fromSamples = real.length === 0 && rows.length > 0;
  const basis = fromSamples ? rows : real;

  const distribution: OrgReviewSummary["distribution"] = [0, 0, 0, 0, 0];
  for (const r of basis) distribution[r.rating - 1]! += 1;
  const average = basis.length
    ? Math.round((basis.reduce((s, r) => s + r.rating, 0) / basis.length) * 10) / 10
    : null;

  // Group by level when given (VP compares across desks), else job title.
  const groups = new Map<string, { label: string; currency: string; amounts: number[] }>();
  for (const r of basis) {
    if (r.salaryAmount == null || !r.salaryCurrency) continue;
    const label = r.grade?.trim() || r.jobTitle.trim();
    const key = `${label.toLowerCase()}|${r.salaryCurrency}`;
    const g = groups.get(key) ?? { label, currency: r.salaryCurrency, amounts: [] };
    g.amounts.push(r.salaryAmount);
    groups.set(key, g);
  }
  const salaries = [...groups.values()]
    .map((g) => {
      const s = g.amounts.sort((a, b) => a - b);
      return {
        label: g.label,
        currency: g.currency,
        count: s.length,
        median: median(s),
        min: s[0]!,
        max: s[s.length - 1]!,
      };
    })
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));

  return { fromSamples, reviewCount: basis.length, average, distribution, salaries };
}
